import React, { useState } from 'react';
import { BottomNav, TopBar } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Download } from 'lucide-react';

const EMICalculator = ({ user }) => {
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [tenure, setTenure] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateEMI = async () => {
    if (!principal || !rate || !tenure) {
      toast.error('Please fill all fields');
      return;
    }

    if (parseFloat(principal) <= 0 || parseFloat(rate) < 0 || parseInt(tenure) <= 0) {
      toast.error('Please enter valid values');
      return;
    }

    setLoading(true);
    try {
      const response = await api.calculateEMI({
        principal: parseFloat(principal),
        rate: parseFloat(rate),
        tenure_months: parseInt(tenure),
      });
      setResult(response.data);
      toast.success('EMI calculated!');
    } catch (error) {
      toast.error('Failed to calculate EMI');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `₹${amount.toLocaleString()}`;

  const handleShare = () => {
    if (!result) return;
    const text = `Home Loan EMI Calculator\n\nLoan Amount: ${formatCurrency(parseFloat(principal))}\nInterest Rate: ${rate}%\nTenure: ${tenure} months\n\nEMI: ${formatCurrency(result.emi)}\nTotal Payment: ${formatCurrency(result.total_payment)}\nTotal Interest: ${formatCurrency(result.total_interest)}`;
    
    if (navigator.share) {
      navigator.share({ title: 'EMI Calculator', text });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-nav">
      <TopBar title="Home Loan EMI Calculator" subtitle="Plan your home purchase" user={user} />

      <div className="p-4 md:p-8 space-y-6">
        {/* Calculator Form */}
        <Card data-testid="emi-calculator-card" className="p-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="principal">Loan Amount (₹)</Label>
              <Input
                id="principal"
                data-testid="principal-input"
                type="number"
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                placeholder="5000000"
              />
            </div>

            <div>
              <Label htmlFor="rate">Interest Rate (% per annum)</Label>
              <Input
                id="rate"
                data-testid="rate-input"
                type="number"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="8.5"
              />
            </div>

            <div>
              <Label htmlFor="tenure">Tenure (months)</Label>
              <Input
                id="tenure"
                data-testid="tenure-input"
                type="number"
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
                placeholder="240"
              />
            </div>

            <Button
              data-testid="calculate-emi-button"
              onClick={calculateEMI}
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Calculating...' : 'Calculate EMI'}
            </Button>
          </div>
        </Card>

        {/* Result */}
        {result && (
          <>
            <Card data-testid="emi-result-card" className="p-6 bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg">
              <div className="text-center mb-6">
                <p className="text-sm opacity-90 mb-2">Monthly EMI</p>
                <p className="text-5xl font-barlow font-bold tabular-nums">
                  {formatCurrency(result.emi)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
                <div>
                  <p className="text-xs opacity-75 mb-1">Total Payment</p>
                  <p className="text-lg font-barlow font-bold tabular-nums">{formatCurrency(result.total_payment)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-75 mb-1">Total Interest</p>
                  <p className="text-lg font-barlow font-bold tabular-nums">{formatCurrency(result.total_interest)}</p>
                </div>
              </div>
              <Button
                data-testid="share-emi-button"
                onClick={handleShare}
                variant="secondary"
                className="w-full mt-6"
              >
                <Download size={16} className="mr-2" />
                Share Summary
              </Button>
            </Card>

            {/* Amortization Schedule */}
            <Card data-testid="amortization-card" className="p-6">
              <h3 className="text-lg font-barlow font-bold uppercase mb-4">Amortization Schedule</h3>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      <th className="text-left p-2 font-semibold text-slate-700">Month</th>
                      <th className="text-right p-2 font-semibold text-slate-700">EMI</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Principal</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Interest</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.schedule.slice(0, 60).map((row) => (
                      <tr key={row.month} className="border-t border-slate-100">
                        <td className="p-2">{row.month}</td>
                        <td className="text-right p-2 font-mono text-xs">{formatCurrency(row.emi)}</td>
                        <td className="text-right p-2 font-mono text-xs text-emerald-600">{formatCurrency(row.principal)}</td>
                        <td className="text-right p-2 font-mono text-xs text-[#5B3DF5]">{formatCurrency(row.interest)}</td>
                        <td className="text-right p-2 font-mono text-xs font-semibold">{formatCurrency(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.schedule.length > 60 && (
                  <p className="text-center text-xs text-slate-500 mt-4">
                    Showing first 60 months. Total tenure: {result.schedule.length} months
                  </p>
                )}
              </div>
            </Card>
          </>
        )}
      </div>

      <BottomNav user={user} />
    </div>
  );
};

export default EMICalculator;
